import { useEffect, useRef, useState } from "react";
import { useParams, Link, useSearchParams } from "react-router-dom";
import { api, formatApiError } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { permsFor } from "@/lib/permissions";
import ClientEditDialog from "@/components/dialogs/ClientEditDialog";
import NewAppointmentDialog from "@/components/dialogs/NewAppointmentDialog";
import NewTaskDialog from "@/components/dialogs/NewTaskDialog";
import NewPaymentDialog from "@/components/dialogs/NewPaymentDialog";
import MessageComposeDialog from "@/components/dialogs/MessageComposeDialog";
import {
  SOURCE_LABEL,
  APPT_TYPE,
  WORK_STATUS,
  WORK_KINDS,
  formatDate,
  formatDateTime,
  formatTime,
  currencyTRY,
  initials,
} from "@/lib/constants";
import { clientJourneyStatusLabel } from "@/lib/clientJourneyStatus";
import AdminDailyProgramPanel from "@/components/admin/AdminDailyProgramPanel";
import ClientDossierPanel from "@/components/admin/ClientDossierPanel";
import {
  ArrowLeft,
  Mail,
  Phone,
  MapPin,
  Calendar,
  Tag,
  Shield,
  Lock,
  Plus,
  Pencil,
  Upload,
  Download,
  FileText,
  BookOpen,
  Eye,
} from "lucide-react";
import { toast } from "sonner";
import {
  Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle,
} from "@/components/ui/dialog";

const TABS = [
  { key: "profil", label: "Profil" },
  { key: "notlar", label: "Notlar" },
  { key: "deneyimler", label: "Deneyimler" },
  { key: "calismalar", label: "Çalışmalar" },
  { key: "danisan-dosyasi", label: "Danışan Dosyası", adminOnly: true },
];

function LabelValue({ label, children, icon: Icon }) {
  return (
    <div>
      <div className="text-[11px] uppercase tracking-wider text-pleiades-muted mb-1 flex items-center gap-1.5">
        {Icon && <Icon className="w-3 h-3" />} {label}
      </div>
      <div className="text-[13px] text-pleiades-ink">{children || "—"}</div>
    </div>
  );
}

function offerStatusLabel(status) {
  return { offered: "Sözleşme kabulü bekleniyor", awaiting_payment: "Havale bekleniyor", active: "Paket etkin" }[status] || status;
}

function offerPrice(offer, key) {
  const keys = { monthly: "monthly_list_price_try", discount: "discount_try", total: "package_price_try" };
  return offer?.[keys[key]];
}

export default function ClientDetailPage() {
  const { id } = useParams();
  const [searchParams] = useSearchParams();
  const { user } = useAuth();
  const perms = permsFor(user);
  const [client, setClient] = useState(null);
  const [tab, setTab] = useState("profil");
  const [notes, setNotes] = useState([]);
  const [sessions, setSessions] = useState([]);
  const [experiences, setExperiences] = useState([]);
  const [works, setWorks] = useState([]);
  const [appts, setAppts] = useState([]);
  const [tasks, setTasks] = useState([]);
  const [payments, setPayments] = useState([]);
  const [messages, setMessages] = useState([]);
  const [files, setFiles] = useState([]);
  const [workAssignments, setWorkAssignments] = useState({ active: null, archive: [], items: [] });
  const [uploading, setUploading] = useState(false);
  const [pdfForm, setPdfForm] = useState({
    title: "", topic: "", description: "", work_days: 7, file: null,
  });
  const pdfUploadFormRef = useRef(null);
  const [replaceTarget, setReplaceTarget] = useState(null);
  const [cancelTarget, setCancelTarget] = useState(null);
  const [cancelReason, setCancelReason] = useState("");
  const [cancelling, setCancelling] = useState(false);
  const [newNote, setNewNote] = useState("");
  const [newNotePrivate, setNewNotePrivate] = useState(true);
  const [editOpen, setEditOpen] = useState(false);
  const [apptOpen, setApptOpen] = useState(false);
  const [taskOpen, setTaskOpen] = useState(false);
  const [payOpen, setPayOpen] = useState(false);
  const [msgOpen, setMsgOpen] = useState(false);
  const [specialPackageMonths, setSpecialPackageMonths] = useState(1);
  const [consultingGuidanceSelected, setConsultingGuidanceSelected] = useState(false);
  const [consultingOffer, setConsultingOffer] = useState(null);
  const [offerSaving, setOfferSaving] = useState(false);
  const [contractLink, setContractLink] = useState(null);
  const [linkSaving, setLinkSaving] = useState(false);
  const [invitationMonths, setInvitationMonths] = useState(1);
  const [invitationDelayMinutes, setInvitationDelayMinutes] = useState(5);
  const [invitationCodeResult, setInvitationCodeResult] = useState(null);
  const [invitationSaving, setInvitationSaving] = useState(false);
  const [previewLinkSaving, setPreviewLinkSaving] = useState(false);
  const [automationJourney, setAutomationJourney] = useState(null);
  const specialPackageActive = Boolean(
    client?.is_special_client
    && client?.special_package_ends_at
    && new Date(client.special_package_ends_at).getTime() > Date.now()
  );

  const loadAll = async () => {
    try {
      const [c, n, s, e, w, a, t, p, m, f, wa] = await Promise.all([
        api.get(`/clients/${id}`),
        api.get(`/clients/${id}/notes`),
        api.get(`/clients/${id}/sessions`),
        ["admin", "danisman"].includes(user?.role)
          ? api.get(`/clients/${id}/experiences`)
          : Promise.resolve({ data: [] }),
        api.get(`/works`, { params: { client_id: id } }),
        api.get(`/appointments`, { params: { client_id: id } }),
        api.get(`/tasks`, { params: { client_id: id } }),
        ["admin", "operasyon"].includes(user?.role)
          ? api.get(`/payments`, { params: { client_id: id } })
          : Promise.resolve({ data: [] }),
        api.get(`/clients/${id}/messages`),
        api.get(`/files`, { params: { client_id: id } }).catch((error) => {
          if (error?.response?.status === 403) return { data: [] };
          throw error;
        }),
        user?.role === "admin"
          ? api.get(`/clients/${id}/work-assignments`)
          : Promise.resolve({ data: { active: null, archive: [], items: [] } }),
      ]);
      setClient(c.data);
      setNotes(n.data);
      setSessions(s.data);
      setExperiences(e.data);
      setWorks(w.data);
      setAppts(a.data);
      setTasks(t.data);
      setPayments(p.data);
      setMessages(m.data);
      setFiles(f.data);
      setWorkAssignments(wa.data);
      if (user?.role === "admin") {
        const [{ data: offers }, { data: journey }] = await Promise.all([
          api.get(`/clients/${id}/consulting-offers`),
          api.get(`/admin/clients/${id}/automation-journey`),
        ]);
        const latest = offers?.[0] || null;
        setConsultingOffer(latest);
        setAutomationJourney(journey);
      }
    } catch (e) {
      toast.error(formatApiError(e));
    }
  };

  const publishPdf = async (activeToReplace = null) => {
    const selected = pdfForm.file;
    if (!selected) return toast.error("Bir PDF dosyası seçin");
    if (selected.type !== "application/pdf" && !selected.name.toLowerCase().endsWith(".pdf")) {
      toast.error("Yalnızca PDF dosyası yükleyebilirsiniz");
      return;
    }
    const form = new FormData();
    form.append("file", selected);
    form.append("client_id", id);
    form.append("category", "danisan_calismasi");
    form.append("title", pdfForm.title);
    form.append("topic", pdfForm.topic);
    form.append("description", pdfForm.description);
    form.append("work_days", String(pdfForm.work_days));
    form.append("rest_days", "0");
    if (activeToReplace) {
      form.append("replace_active_assignment_id", activeToReplace.assignment_id);
      form.append("replace_active_expected_version", String(activeToReplace.version));
    }
    const idempotencyKey = globalThis.crypto?.randomUUID?.() || `admin-${Date.now()}-${Math.random().toString(16).slice(2)}`;
    setUploading(true);
    try {
      await api.post("/files", form, {
        headers: { "Content-Type": "multipart/form-data", "Idempotency-Key": idempotencyKey },
      });
      setPdfForm({ title: "", topic: "", description: "", work_days: 7, file: null });
      pdfUploadFormRef.current?.reset();
      setReplaceTarget(null);
      await loadAll();
      toast.success(activeToReplace
        ? "Yeni PDF aktif edildi; önceki çalışma silinmeden arşivlendi"
        : "Kişisel çalışma PDF’si yayınlandı");
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setUploading(false);
    }
  };

  const requestPdfPublish = (event) => {
    event.preventDefault();
    const selected = pdfForm.file;
    if (!selected) {
      toast.error("Bir PDF dosyası seçin");
      return;
    }
    if (selected.type !== "application/pdf" && !selected.name.toLowerCase().endsWith(".pdf")) {
      toast.error("Yalnızca PDF dosyası yükleyebilirsiniz");
      return;
    }
    if (workAssignments.active) {
      setReplaceTarget(workAssignments.active);
      return;
    }
    publishPdf();
  };

  const cancelActiveWork = async () => {
    if (!cancelTarget || cancelReason.trim().length < 5) return;
    setCancelling(true);
    try {
      await api.post(`/clients/${id}/work-assignments/${cancelTarget.assignment_id}/cancel`, {
        reason: cancelReason.trim(),
        expected_version: cancelTarget.version,
      });
      setCancelTarget(null);
      setCancelReason("");
      await loadAll();
      toast.success("Çalışma sonlandırıldı; yeni çalışma yayınlama kilidi açıldı");
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setCancelling(false);
    }
  };

  const sendConsultingOffer = async () => {
    setOfferSaving(true);
    try {
      const { data } = await api.post(`/clients/${id}/consulting-offers`, {
        package_months: specialPackageMonths,
        guidance_selected: consultingGuidanceSelected,
      });
      setConsultingOffer(data);
      toast.success("Program teklifi kullanıcı paneline gönderildi");
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setOfferSaving(false);
    }
  };

  const createContractLink = async () => {
    setLinkSaving(true);
    try {
      const { data } = await api.post(`/consulting-offers/${consultingOffer.id}/contract-link`);
      setContractLink({ ...data, url: new URL(data.contract_path, window.location.origin).toString() });
      toast.success("Kişisel sözleşme bağlantısı oluşturuldu");
    } catch (e) { toast.error(formatApiError(e)); } finally { setLinkSaving(false); }
  };

  const openClientPanelPreview = async () => {
    const previewWindow = window.open("about:blank", "_blank");
    setPreviewLinkSaving(true);
    try {
      const { data } = await api.post(`/admin/clients/${id}/panel-preview-links`);
      if (previewWindow) {
        previewWindow.opener = null;
        previewWindow.location.replace(data.preview_url);
      } else {
        window.location.assign(data.preview_url);
      }
      toast.success("Salt okunur danışan paneli önizlemesi açıldı");
    } catch (error) {
      previewWindow?.close();
      toast.error(formatApiError(error));
    } finally {
      setPreviewLinkSaving(false);
    }
  };

  const copyText = async (text, message) => {
    try { await navigator.clipboard.writeText(text); toast.success(message); }
    catch { toast.error("Panoya kopyalanamadı; metni seçerek kopyalayın"); }
  };

  const verifyOfferPayment = async (event) => {
    event.preventDefault();
    const paymentReportId = searchParams.get("payment_report") || consultingOffer.latest_receipt_id;
    if (!paymentReportId) {
      toast.error("Kullanıcının ödeme bildirimi bulunamadı");
      return;
    }
    setOfferSaving(true);
    try {
      const { data } = await api.patch(`/consulting-offers/${consultingOffer.id}/verify-payment`, {
        payment_report_id: paymentReportId,
      });
      setConsultingOffer(data);
      await loadAll();
      toast.success("Ödeme onaylandı ve paket etkinleştirildi");
    } catch (e) { toast.error(formatApiError(e)); } finally { setOfferSaving(false); }
  };

  const createInvitationCode = async (event) => {
    event.preventDefault();
    setInvitationSaving(true);
    try {
      const { data } = await api.post(`/admin/clients/${id}/invitation-codes`, {
        package_months: Number(invitationMonths),
        preparation_delay_minutes: Number(invitationDelayMinutes),
      });
      setInvitationCodeResult(data);
      toast.success("Tek kullanımlık davet kodu oluşturuldu");
    } catch (error) {
      toast.error(formatApiError(error));
    } finally {
      setInvitationSaving(false);
    }
  };

  const removeSpecialClient = async () => {
    if (!window.confirm("Özel danışmanlık statüsü ve aktif paket kaldırılsın mı?")) return;
    try {
      const { data } = await api.patch(`/clients/${id}/special-status`, { is_special_client: false });
      setClient((current) => ({ ...current, ...data }));
      toast.success("Özel danışanlık kaldırıldı");
    } catch (e) {
      toast.error(formatApiError(e));
    }
  };

  const openFile = async (file) => {
    try {
      const { data } = await api.get(`/files/${file.id}`, { responseType: "blob" });
      const url = URL.createObjectURL(data);
      window.open(url, "_blank", "noopener,noreferrer");
      setTimeout(() => URL.revokeObjectURL(url), 60000);
    } catch (e) {
      toast.error(formatApiError(e));
    }
  };

  useEffect(() => {
    loadAll();
    // eslint-disable-next-line
  }, [id]);

  useEffect(() => {
    if (user?.role !== "admin") return undefined;
    let active = true;
    const refreshAccess = async () => {
      try {
        const [clientResult, assignmentsResult, journeyResult] = await Promise.all([
          api.get(`/clients/${id}`),
          api.get(`/clients/${id}/work-assignments`),
          api.get(`/admin/clients/${id}/automation-journey`),
        ]);
        if (!active) return;
        setClient(clientResult.data);
        setWorkAssignments(assignmentsResult.data);
        setAutomationJourney(journeyResult.data);
      } catch {
        // İlk yükleme hata bildirimini yönetir; arka plan eşitlemesi sessiz kalır.
      }
    };
    const timer = window.setInterval(refreshAccess, 5000);
    const onFocus = () => refreshAccess();
    window.addEventListener("focus", onFocus);
    return () => {
      active = false;
      window.clearInterval(timer);
      window.removeEventListener("focus", onFocus);
    };
  }, [id, user?.role]);

  useEffect(() => {
    if (client?.special_package_months) setSpecialPackageMonths(client.special_package_months);
  }, [client?.special_package_months]);

  useEffect(() => {
    const requestedTab = searchParams.get("tab");
    const experienceId = searchParams.get("experience");
    const paymentReportId = searchParams.get("payment_report");
    if (requestedTab === "deneyimler") setTab("deneyimler");
    if (experienceId && user?.role === "admin") {
      api.patch(`/experiences/${experienceId}/read`).catch(() => {});
      setTimeout(() => document.getElementById(`experience-${experienceId}`)?.scrollIntoView({ behavior: "smooth", block: "center" }), 250);
    }
    if (paymentReportId && user?.role === "admin") {
      setTimeout(() => {
        const section = document.getElementById("payment-approval");
        section?.scrollIntoView({ behavior: "smooth", block: "center" });
        section?.focus({ preventScroll: true });
      }, 250);
    }
  }, [searchParams, user?.role]);

  const addNote = async () => {
    if (!newNote.trim()) return;
    try {
      const { data } = await api.post(`/clients/${id}/notes`, {
        body: newNote,
        is_private: newNotePrivate,
      });
      setNotes([data, ...notes]);
      setNewNote("");
      toast.success("Not eklendi");
    } catch (e) {
      toast.error(formatApiError(e));
    }
  };

  if (!client) {
    return <div className="p-3 sm:p-5 lg:p-6 text-[13px] text-pleiades-slate">Yükleniyor…</div>;
  }

  return (
    <div className="p-3 sm:p-5 lg:p-6 max-w-[1200px]" data-testid="client-detail-page">
      <Link
        to="/danisanlar"
        className="inline-flex items-center gap-1 text-[12px] text-pleiades-slate hover:text-pleiades-ink mb-3"
        data-testid="back-to-clients"
      >
        <ArrowLeft className="w-3.5 h-3.5" /> Danışanlar
      </Link>

      {/* Header */}
      <div className="border border-pleiades-border bg-white rounded-sm p-3 sm:p-5 mb-3 sm:mb-4">
        <div className="flex items-start justify-between gap-4 flex-wrap">
          <div className="flex items-center gap-4">
            <div className="pleiades-initials-avatar w-14 h-14 rounded-sm text-lg font-medium flex items-center justify-center">
              {initials(client.full_name)}
            </div>
            <div>
              <h1 className="text-2xl font-semibold tracking-tight" data-testid="client-name">
                {client.full_name}
              </h1>
              <div className={`inline-flex mt-1 px-2 py-1 text-[10px] border ${client.is_special_client ? "bg-emerald-50 border-emerald-200 text-emerald-800" : "bg-slate-50 border-slate-200 text-slate-600"}`}>
                {client.is_special_client ? "Özel Danışan" : "Normal Kullanıcı"}
              </div>
              {client.is_special_client && client.special_package_started_at && <div className="text-[11px] text-pleiades-slate mt-2">
                {client.special_package_days ? `${client.special_package_days} günlük davet paketi` : `${client.special_package_months} aylık paket`} · {new Date(client.special_package_started_at).toLocaleDateString("tr-TR")} – {new Date(client.special_package_ends_at).toLocaleDateString("tr-TR")}
              </div>}
              <div className="mt-1 flex items-center gap-3 text-[12px] text-pleiades-slate flex-wrap">
                {client.email && (
                  <span className="flex items-center gap-1"><Mail className="w-3 h-3" /> {client.email}</span>
                )}
                {client.phone && (
                  <span className="flex items-center gap-1"><Phone className="w-3 h-3" /> {client.phone}</span>
                )}
                {client.city && (
                  <span className="flex items-center gap-1"><MapPin className="w-3 h-3" /> {client.city}</span>
                )}
              </div>
            </div>
          </div>
          <div className="flex items-center gap-2">
            {user?.role === "admin" && (
              <button
                type="button"
                onClick={openClientPanelPreview}
                disabled={previewLinkSaving}
                className="mt-4 inline-flex min-h-8 items-center gap-1.5 border border-pleiades-blue/40 bg-pleiades-blue-soft px-3 text-[12px] font-medium text-pleiades-blue disabled:opacity-50"
              >
                <Eye className="h-3.5 w-3.5" />
                {previewLinkSaving ? "Hazırlanıyor…" : "Paneli görüntüle"}
              </button>
            )}
            {false && user?.role === "admin" && <div className="mt-4 flex items-center gap-2 flex-wrap">
              <select value={specialPackageMonths} onChange={(e) => setSpecialPackageMonths(Number(e.target.value))} className="h-8 px-2 border border-pleiades-border rounded-sm text-[12px] bg-white">
                <option value={1}>1 aylık</option><option value={3}>3 aylık</option><option value={6}>6 aylık</option><option value={12}>1 yıllık</option>
              </select>
              <label className="h-8 px-2 border border-pleiades-border rounded-sm text-[12px] bg-white inline-flex items-center gap-2">
                <input
                  type="checkbox"
                  checked={consultingGuidanceSelected}
                  onChange={(event) => setConsultingGuidanceSelected(event.target.checked)}
                />
                Rehberlik ekle (+5.000 TL)
              </label>
              <button onClick={sendConsultingOffer} disabled={offerSaving} className="h-8 px-3 border border-emerald-300 bg-emerald-50 text-emerald-800 rounded-sm text-[12px] disabled:opacity-50">
                {offerSaving ? "Gönderiliyor…" : "Teklifi ve sözleşmeyi gönder"}
              </button>
              {client.is_special_client && <button onClick={removeSpecialClient} className="h-8 px-3 border border-slate-300 bg-white rounded-sm text-[12px]">Özel danışmanlığı kaldır</button>}
            </div>}
            {user?.role === "admin" && <div>
              <div className="text-[10px] uppercase tracking-wider text-pleiades-muted mb-1">
                Süreç durumu
              </div>
              <div
                data-testid="client-journey-status"
                className="min-h-8 px-3 py-2 border border-pleiades-border bg-pleiades-sidebar/50 text-[12px] text-pleiades-ink"
              >
                {clientJourneyStatusLabel(automationJourney)}
              </div>
            </div>}
            {perms.canEditClient && (
              <button
                onClick={() => setEditOpen(true)}
                data-testid="btn-edit-client"
                className="h-8 px-3 border border-pleiades-border rounded-sm text-[12px] hover:bg-pleiades-sidebar flex items-center gap-1.5 mt-4"
              >
                <Pencil className="w-3.5 h-3.5" /> Düzenle
              </button>
            )}
          </div>
        </div>

        {(client.tags || []).length > 0 && (
          <div className="flex flex-wrap gap-1.5 mt-4">
            {client.tags.map((t, i) => (
              <span
                key={i}
                className="text-[11px] px-2 py-0.5 bg-pleiades-gold-soft text-[#8a6f37] border border-pleiades-gold/30 rounded-sm inline-flex items-center gap-1"
              >
                <Tag className="w-2.5 h-2.5" /> {t}
              </span>
            ))}
          </div>
        )}
      </div>

      {user?.role === "admin" && <section className="border border-pleiades-border bg-white p-3 sm:p-4 mb-3 sm:mb-4">
        <div className="text-[10px] uppercase tracking-wider text-pleiades-muted">Davet erişimi</div>
        <h2 className="text-[14px] font-semibold mt-1">Kullanıcıya özel program davet kodu</h2>
        <p className="text-[12px] leading-[1.6] text-pleiades-slate mt-2">
          Kod tek kullanımlıdır ve yalnız bu danışanın hesabında çalışır. Kullanıcı kendi hesabına giriş yaptıktan sonra kodu kullanır; ödeme veya ticari sözleşme oluşturulmaz.
        </p>
        <p role="status" className="mt-3 text-[12px] font-medium text-pleiades-blue">
          {clientJourneyStatusLabel(automationJourney)}
        </p>
        <form onSubmit={createInvitationCode} className="mt-4 grid sm:grid-cols-[1fr_1fr_auto] gap-2">
          <label className="flex-1 text-[11px] text-pleiades-muted">
            Program süresi (ay)
            <select value={invitationMonths} onChange={(event) => setInvitationMonths(event.target.value)} className="mt-1 min-h-11 w-full border border-pleiades-border px-3 text-[13px] bg-white">
              <option value="1">1 aylık · 30 gün</option>
              <option value="3">3 aylık · 90 gün</option>
              <option value="6">6 aylık · 180 gün</option>
              <option value="12">12 aylık · 365 gün</option>
            </select>
          </label>
          <label className="flex-1 text-[11px] text-pleiades-muted">
            Anket sonrası hazırlık süresi (dakika)
            <input type="number" min="1" max="1440" value={invitationDelayMinutes} onChange={(event) => setInvitationDelayMinutes(event.target.value)} className="mt-1 min-h-11 w-full border border-pleiades-border px-3 text-[13px]" />
          </label>
          <button disabled={invitationSaving || ![1, 3, 6, 12].includes(Number(invitationMonths)) || Number(invitationDelayMinutes) < 1 || Number(invitationDelayMinutes) > 1440} className="self-end min-h-11 px-5 bg-pleiades-blue text-white text-[12px] disabled:opacity-40">
            {invitationSaving ? "Oluşturuluyor…" : "Davet kodu oluştur"}
          </button>
        </form>
        <p className="mt-2 text-[10px] text-pleiades-muted">
          Sayaç anket tamamlandığında başlar. Süre dolunca ilk çalışma otomatik olarak kullanıcı panelinde açılır.
        </p>
        {invitationCodeResult && <div className="mt-4 border border-pleiades-blue/25 bg-pleiades-blue-soft p-4">
          <div className="text-[10px] uppercase tracking-wider text-pleiades-muted">Yalnız bir kez gösterilir</div>
          <div className="mt-2 font-mono text-[18px] tracking-[.14em] break-all">{invitationCodeResult.code}</div>
          <p className="mt-2 text-[12px] text-pleiades-slate">
            {invitationCodeResult.package_months} aylık erişim · anket sonrası {invitationCodeResult.preparation_delay_minutes} dakika planlanan hazırlık
          </p>
          <div className="mt-3 flex flex-wrap gap-2">
            <button type="button" onClick={() => copyText(invitationCodeResult.code, "Davet kodu kopyalandı")} className="min-h-10 px-4 border border-pleiades-border bg-white text-[12px]">Kodu kopyala</button>
            <button type="button" onClick={() => copyText(`Merhaba ${client.full_name}, ${invitationCodeResult.package_months} aylık Pleiades program erişiminiz için davet kodunuz: ${invitationCodeResult.code}\\n\\nGüvenli giriş: https://pleiadesrehberlik.com/giris\\nKendi hesabınıza giriş yaptıktan sonra kodu kullanabilirsiniz.`, "WhatsApp metni kopyalandı")} className="min-h-10 px-4 bg-[#1f7a52] text-white text-[12px]">WhatsApp metnini kopyala</button>
          </div>
          <p className="mt-2 text-[10px] text-pleiades-muted">Mesaj otomatik gönderilmez.</p>
        </div>}
      </section>}

      {user?.role === "admin" && <AdminDailyProgramPanel journey={automationJourney} refresh={loadAll} />}

      {false && user?.role === "admin" && consultingOffer && <section id="payment-approval" tabIndex={-1} className={`border bg-white p-4 mb-4 focus:outline-none ${searchParams.get("payment_report") ? "border-pleiades-blue ring-2 ring-pleiades-blue/15" : "border-pleiades-border"}`}>
        <div className="flex justify-between gap-3 flex-wrap"><div><div className="text-[10px] uppercase tracking-wider text-pleiades-muted">Son program teklifi</div><h2 className="text-[14px] font-semibold mt-1">{consultingOffer.package_months === 12 ? "1 yıllık" : `${consultingOffer.package_months} aylık`} dijital rehberlik</h2></div><span className="text-[10px] px-2 py-1 border border-pleiades-border h-fit">{offerStatusLabel(consultingOffer.status)}</span></div>
        <div className="grid sm:grid-cols-4 gap-3 mt-4">
          <LabelValue label="Dijital program PDF’si">{currencyTRY(consultingOffer.personalized_plan_price_try ?? consultingOffer.digital_program_price_try)}</LabelValue>
          <LabelValue label="Seçimlik rehberlik">{consultingOffer.guidance_selected ? currencyTRY(consultingOffer.guidance_service_price_try) : "Seçilmedi"}</LabelValue>
          <LabelValue label="Süre indirimi">{currencyTRY(offerPrice(consultingOffer, "discount"))}</LabelValue>
          <LabelValue label="Toplam">{currencyTRY(offerPrice(consultingOffer, "total"))}</LabelValue>
        </div>
        {consultingOffer.status === "offered" && <div className="mt-4 pt-4 border-t border-pleiades-border">
          <div className="flex justify-between gap-3 flex-wrap"><p className="text-[11px] text-pleiades-slate">Kişisel bağlantı 7 gün geçerlidir; yenileme önceki bağlantıyı geçersiz kılar.</p><button onClick={createContractLink} disabled={linkSaving} className="h-9 px-3 border border-pleiades-blue text-pleiades-blue text-[12px] disabled:opacity-50">{linkSaving ? "Üretiliyor…" : contractLink ? "Bağlantıyı yenile" : "Bağlantı üret"}</button></div>
          {contractLink && <div className="mt-3 p-3 bg-pleiades-ivory border border-pleiades-border">
            <div className="flex gap-2"><input readOnly value={contractLink.url} onFocus={(e) => e.currentTarget.select()} className="h-10 px-3 min-w-0 flex-1 bg-white border border-pleiades-border text-[12px]" /><button onClick={() => copyText(contractLink.url, "Bağlantı kopyalandı")} className="h-10 px-3 bg-pleiades-ink text-white text-[12px]">Kopyala</button></div>
            <p className="text-[12px] text-pleiades-slate whitespace-pre-wrap mt-3">{`Merhaba ${client.full_name}, dijital rehberlik programınıza ait fiyat özeti ve sözleşmeleri güvenli hesabınızdan inceleyebilirsiniz:\n${contractLink.url}\n\nBağlantı 7 gün geçerlidir. Kendi Pleiades danışan hesabınızla giriş yapmanız gerekir.`}</p>
            <button onClick={() => copyText(`Merhaba ${client.full_name}, dijital rehberlik programınıza ait fiyat özeti ve sözleşmeleri güvenli hesabınızdan inceleyebilirsiniz:\n${contractLink.url}\n\nBağlantı 7 gün geçerlidir. Kendi Pleiades danışan hesabınızla giriş yapmanız gerekir.`, "WhatsApp metni kopyalandı")} className="mt-2 h-9 px-3 border border-pleiades-border bg-white text-[12px]">WhatsApp metnini kopyala</button>
            <p className="text-[10px] text-pleiades-muted mt-2">Mesaj otomatik gönderilmez.</p>
          </div>}
        </div>}
        {consultingOffer.status === "awaiting_payment" && <form onSubmit={verifyOfferPayment} className="mt-4 pt-4 border-t border-pleiades-border">
          <div className="grid sm:grid-cols-3 gap-3 mb-4">
            <LabelValue label="Beklenen tutar">{currencyTRY(consultingOffer.package_price_try)}</LabelValue>
            <LabelValue label="Havale referansı">{consultingOffer.transfer_reference || "Eksik"}</LabelValue>
            <LabelValue label="Panel kullanıcısı">{client.full_name}</LabelValue>
            <LabelValue label="Sözleşme ve beyan edilen havale adı">{consultingOffer.payment_sender_full_name || consultingOffer.contract_party_full_name || "Kullanıcının kimlik beyanı bekleniyor"}</LabelValue>
          </div>
          <p className="mb-3 text-[11px] leading-5 text-pleiades-slate">
            Banka hareketinde tutar, havale referansı ve gönderen adını kontrol ettikten sonra onaylayın.
          </p>
          <div>
            <button disabled={offerSaving || !(searchParams.get("payment_report") || consultingOffer.latest_receipt_id) || !(consultingOffer.payment_sender_full_name || consultingOffer.contract_party_full_name)} className="min-h-11 px-5 bg-pleiades-blue text-white text-[12px] disabled:opacity-40">
              {offerSaving ? "Onaylanıyor…" : "Ödemeyi onayla"}
            </button>
          </div>
          {!(searchParams.get("payment_report") || consultingOffer.latest_receipt_id) && <p role="alert" className="mt-3 text-[11px] text-amber-800">Kullanıcı henüz “Ödeme yaptım” bildirimi göndermedi. Admin onayı verilemez.</p>}
          {!(consultingOffer.payment_sender_full_name || consultingOffer.contract_party_full_name) && <p role="alert" className="mt-3 text-[11px] text-amber-800">Kullanıcının sözleşme ve havale adı henüz tamamlanmadı. Program başlatılamaz.</p>}
        </form>}
      </section>}

      {/* Tabs */}
      <div className="border-b border-pleiades-border mb-4" data-testid="detail-tabs">
        <div className="flex gap-1 overflow-x-auto">
          {TABS.filter((t) => !t.adminOnly || user?.role === "admin").map((t) => (
            <button
              key={t.key}
              onClick={() => setTab(t.key)}
              data-testid={`tab-${t.key}`}
              className={`px-3 py-2 text-[12px] font-medium whitespace-nowrap transition-colors ${
                tab === t.key
                  ? "text-pleiades-ink tab-active-gold"
                  : "text-pleiades-slate hover:text-pleiades-ink"
              }`}
            >
              {t.label}
            </button>
          ))}
        </div>
      </div>

      {/* Tab bodies */}
      {tab === "danisan-dosyasi" && user?.role === "admin" && <ClientDossierPanel clientId={id} />}

      {tab === "profil" && (
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div className="border border-pleiades-border bg-white rounded-sm p-4 space-y-4">
            <div className="text-[12px] font-semibold text-pleiades-ink">İletişim ve Kimlik</div>
            <LabelValue label="Ad Soyad">{client.full_name}</LabelValue>
            <LabelValue label="E-posta">{client.email}</LabelValue>
            <LabelValue label="Telefon">{client.phone}</LabelValue>
            <LabelValue label="Şehir">{client.city}</LabelValue>
            <LabelValue label="Doğum Tarihi" icon={Calendar}>
              {client.birth_date ? formatDate(client.birth_date, { withYear: true }) : "—"}
            </LabelValue>
          </div>
          <div className="border border-pleiades-border bg-white rounded-sm p-4 space-y-4">
            <div className="text-[12px] font-semibold text-pleiades-ink">Danışmanlık Durumu</div>
            {user?.role === "admin" && <LabelValue label="Süreç Durumu">
              {clientJourneyStatusLabel(automationJourney)}
            </LabelValue>}
            <LabelValue label="Kaynak">{SOURCE_LABEL[client.source] || client.source}</LabelValue>
            <LabelValue label="KVKK Onayı" icon={Shield}>
              {client.consent ? (
                <span className="text-green-700">Verildi</span>
              ) : (
                <span className="text-amber-700">Verilmedi</span>
              )}
            </LabelValue>
            <LabelValue label="Kayıt Tarihi">{formatDateTime(client.created_at)}</LabelValue>
            <LabelValue label="Son Güncelleme">{formatDateTime(client.updated_at)}</LabelValue>
          </div>
        </div>
      )}

      {tab === "notlar" && (
        <div>
          <div className="border border-pleiades-border bg-white rounded-sm p-3 mb-3">
            <textarea
              value={newNote}
              onChange={(e) => setNewNote(e.target.value)}
              placeholder="Not ekleyin…"
              rows={3}
              data-testid="new-note-body"
              className="w-full text-[13px] border border-pleiades-border rounded-sm p-2 focus:outline-none focus:border-pleiades-blue resize-none"
            />
            <div className="flex items-center justify-between mt-2">
              <label className="flex items-center gap-2 text-[12px] text-pleiades-slate">
                <input
                  type="checkbox"
                  checked={newNotePrivate}
                  onChange={(e) => setNewNotePrivate(e.target.checked)}
                  data-testid="new-note-private"
                />
                <Lock className="w-3 h-3" /> Sadece dahili
              </label>
              <button
                onClick={addNote}
                data-testid="add-note-submit"
                className="h-8 px-3 bg-pleiades-blue hover:bg-pleiades-blue-hover text-white text-[12px] rounded-sm"
              >
                Not Ekle
              </button>
            </div>
          </div>
          <div className="space-y-2">
            {notes.length === 0 && (
              <div className="text-center text-[12px] text-pleiades-slate py-10 border border-dashed border-pleiades-border rounded-sm">
                Henüz not eklenmemiş.
              </div>
            )}
            {notes.map((n) => (
              <div
                key={n.id}
                className="border border-pleiades-border bg-white rounded-sm p-3"
                data-testid={`note-${n.id}`}
              >
                <div className="flex items-center gap-2 mb-1.5">
                  <span className="text-[12px] font-medium">{n.author_name || "—"}</span>
                  <span className="text-[11px] text-pleiades-muted">{formatDateTime(n.created_at)}</span>
                  {n.is_private && (
                    <span className="text-[10px] uppercase tracking-wider px-1.5 py-0.5 bg-red-50 text-red-700 border border-red-200 rounded-sm inline-flex items-center gap-1">
                      <Lock className="w-2.5 h-2.5" /> Dahili
                    </span>
                  )}
                </div>
                <div className="text-[13px] text-pleiades-ink whitespace-pre-wrap">{n.body}</div>
              </div>
            ))}
          </div>
        </div>
      )}

      {tab === "seanslar" && (
        <div className="space-y-2">
          {sessions.length === 0 && (
            <div className="text-center text-[12px] text-pleiades-slate py-10 border border-dashed border-pleiades-border rounded-sm">
              Kayıtlı seans yok.
            </div>
          )}
          {sessions.map((s) => (
            <div key={s.id} className="border border-pleiades-border bg-white rounded-sm p-3" data-testid={`session-${s.id}`}>
              <div className="flex items-center justify-between mb-1">
                <span className="text-[12px] font-medium">{formatDateTime(s.started_at)}</span>
                <span className="text-[11px] text-pleiades-muted">{s.duration_minutes} dk</span>
              </div>
              <div className="text-[13px] text-pleiades-ink">{s.summary}</div>
              {s.private_notes && (
                <div className="mt-2 p-2 bg-red-50/40 border border-red-100 rounded-sm text-[12px] text-red-800">
                  <span className="text-[10px] uppercase tracking-wider inline-flex items-center gap-1 mr-2">
                    <Lock className="w-2.5 h-2.5" /> Dahili
                  </span>
                  {s.private_notes}
                </div>
              )}
            </div>
          ))}
        </div>
      )}

      {tab === "deneyimler" && (
        <div>
          <div className="mb-4">
            <div className="text-[13px] font-semibold flex items-center gap-2">
              <BookOpen className="w-4 h-4" /> Danışanın deneyim defteri
            </div>
            <div className="text-[11px] text-pleiades-muted mt-1">
              Danışanın kendi panelinden yazdığı deneyimler, en yeni kayıt önce olacak şekilde gösterilir.
            </div>
          </div>
          <div className="space-y-3">
            {experiences.length === 0 && <EmptyState text="Danışan henüz bir deneyim yazmamış." />}
            {experiences.map((experience) => (
              <article
                key={experience.id}
                id={`experience-${experience.id}`}
                className={`border bg-white rounded-sm p-4 transition-shadow ${
                  searchParams.get("experience") === experience.id
                    ? "border-pleiades-blue ring-2 ring-pleiades-blue/20"
                    : "border-pleiades-border"
                }`}
              >
                <div className="flex items-start justify-between gap-4 mb-3">
                  <div>
                    <div className="text-[13px] font-semibold">{experience.title}</div>
                    <div className="text-[11px] text-pleiades-muted mt-1">
                      {formatDateTime(experience.created_at)}
                      {experience.updated_at !== experience.created_at ? " · Düzenlendi" : ""}
                    </div>
                  </div>
                  <span className="text-[10px] uppercase tracking-wider px-2 py-1 bg-pleiades-ivory border border-pleiades-border">
                    Danışan yazısı
                  </span>
                </div>
                <div className="text-[13px] leading-relaxed text-pleiades-slate whitespace-pre-wrap">
                  {experience.body}
                </div>
              </article>
            ))}
          </div>
        </div>
      )}

      {tab === "calismalar" && (
        <div className="space-y-2">
          {works.length === 0 && <EmptyState text="Kayıtlı çalışma yok." />}
          {works.map((w) => (
            <div key={w.id} className="border border-pleiades-border bg-white rounded-sm p-3" data-testid={`work-${w.id}`}>
              <div className="flex items-center justify-between">
                <div>
                  <div className="text-[13px] font-medium">{w.title}</div>
                  <div className="text-[11px] text-pleiades-slate mt-0.5">
                    {WORK_KINDS[w.kind]} · {w.scheduled_date ? formatDate(w.scheduled_date) : "—"}
                  </div>
                </div>
                <span className="text-[10px] uppercase tracking-wider px-1.5 py-0.5 border border-pleiades-border bg-pleiades-sidebar rounded-sm">
                  {WORK_STATUS[w.status]}
                </span>
              </div>
              {w.description && (
                <div className="text-[12px] text-pleiades-slate mt-2">{w.description}</div>
              )}
            </div>
          ))}
        </div>
      )}

      {tab === "randevular" && (
        <div>
          <div className="flex justify-end mb-2">
            <button
              onClick={() => setApptOpen(true)}
              data-testid="btn-add-appt"
              className="h-8 px-3 bg-pleiades-blue hover:bg-pleiades-blue-hover text-white text-[12px] rounded-sm flex items-center gap-1.5"
            >
              <Plus className="w-3.5 h-3.5" /> Randevu Ekle
            </button>
          </div>
          <div className="space-y-2">
            {appts.length === 0 && <EmptyState text="Randevu yok." />}
            {appts.map((a) => (
              <div key={a.id} className="border border-pleiades-border bg-white rounded-sm p-3 flex items-center gap-3" data-testid={`appt-${a.id}`}>
                <div className="w-16 text-center">
                  <div className="text-[10px] uppercase tracking-wider text-pleiades-muted">
                    {formatDate(a.starts_at)}
                  </div>
                  <div className="text-[15px] font-semibold tabular-nums">{formatTime(a.starts_at)}</div>
                </div>
                <div className="flex-1">
                  <div className="text-[13px] font-medium">{APPT_TYPE[a.type]}</div>
                  <div className="text-[11px] text-pleiades-slate">{a.location === "online" ? "Online" : "Ofis"} · {a.duration_minutes} dk</div>
                </div>
                <span className="text-[10px] uppercase tracking-wider px-1.5 py-0.5 border border-pleiades-border bg-pleiades-sidebar rounded-sm">
                  {a.status}
                </span>
              </div>
            ))}
          </div>
        </div>
      )}

      {tab === "gorevler" && (
        <div>
          <div className="flex justify-end mb-2">
            <button
              onClick={() => setTaskOpen(true)}
              data-testid="btn-add-task"
              className="h-8 px-3 bg-pleiades-blue hover:bg-pleiades-blue-hover text-white text-[12px] rounded-sm flex items-center gap-1.5"
            >
              <Plus className="w-3.5 h-3.5" /> Görev Ekle
            </button>
          </div>
          <div className="space-y-2">
            {tasks.length === 0 && <EmptyState text="Görev yok." />}
            {tasks.map((t) => (
              <div key={t.id} className="border border-pleiades-border bg-white rounded-sm p-3" data-testid={`task-${t.id}`}>
                <div className="flex items-center justify-between">
                  <div className="text-[13px]">{t.title}</div>
                  <span className="text-[11px] text-pleiades-slate">{formatDate(t.due_date)}</span>
                </div>
              </div>
            ))}
          </div>
        </div>
      )}

      {tab === "odemeler" && (
        <div>
          {perms.canCreatePayment && (
            <div className="flex justify-end mb-2">
              <button
                onClick={() => setPayOpen(true)}
                data-testid="btn-add-payment"
                className="h-8 px-3 bg-pleiades-blue hover:bg-pleiades-blue-hover text-white text-[12px] rounded-sm flex items-center gap-1.5"
              >
                <Plus className="w-3.5 h-3.5" /> Ödeme Ekle
              </button>
            </div>
          )}
          <div className="border border-pleiades-border bg-white rounded-sm overflow-hidden">
            <table className="w-full text-[13px]">
              <thead className="bg-pleiades-sidebar/60 border-b border-pleiades-border text-[11px] uppercase tracking-wider text-pleiades-slate">
                <tr>
                  <th className="text-left font-medium px-3 py-2">Açıklama</th>
                  <th className="text-left font-medium px-3 py-2">Vade</th>
                  <th className="text-left font-medium px-3 py-2">Durum</th>
                  <th className="text-right font-medium px-3 py-2">Tutar</th>
                </tr>
              </thead>
              <tbody>
                {payments.length === 0 && (
                  <tr><td colSpan={4} className="text-center py-8 text-pleiades-slate text-[12px]">Ödeme yok.</td></tr>
                )}
                {payments.map((p) => (
                  <tr key={p.id} className="border-b border-pleiades-border/60" data-testid={`payment-${p.id}`}>
                    <td className="px-3 py-2">{p.description || "—"}</td>
                    <td className="px-3 py-2 text-pleiades-slate">{formatDate(p.due_date)}</td>
                    <td className="px-3 py-2">
                      <span className={"text-[10px] uppercase tracking-wider px-1.5 py-0.5 rounded-sm border " + (p.status === "odendi" ? "bg-green-50 text-green-700 border-green-200" : "bg-amber-50 text-amber-700 border-amber-200")}>
                        {p.status === "odendi" ? "Ödendi" : "Beklemede"}
                      </span>
                    </td>
                    <td className="px-3 py-2 text-right font-medium tabular-nums">{currencyTRY(p.amount)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {tab === "mesajlar" && (
        <div>
          <div className="flex justify-end mb-2">
            <button
              onClick={() => setMsgOpen(true)}
              data-testid="btn-add-msg"
              className="h-8 px-3 bg-pleiades-blue hover:bg-pleiades-blue-hover text-white text-[12px] rounded-sm flex items-center gap-1.5"
            >
              <Plus className="w-3.5 h-3.5" /> Mesaj Yaz
            </button>
          </div>
          <div className="space-y-2">
            {messages.length === 0 && <EmptyState text="Mesaj yok." />}
            {messages.map((m) => (
              <div key={m.id} className={"border border-pleiades-border rounded-sm p-3 " + (m.direction === "inbound" ? "bg-white" : "bg-pleiades-blue-soft")} data-testid={`msg-${m.id}`}>
                <div className="flex items-center justify-between mb-1">
                  <span className="text-[11px] uppercase tracking-wider text-pleiades-slate">
                    {m.direction === "inbound" ? "Gelen" : "Giden"} · {m.channel}
                  </span>
                  <span className="text-[11px] text-pleiades-muted">{formatDateTime(m.created_at)}</span>
                </div>
                <div className="text-[13px]">{m.body}</div>
              </div>
            ))}
          </div>
        </div>
      )}

      {tab === "dosyalar" && (
        <div>
          <div className="mb-3">
            <div>
              <div className="text-[13px] font-semibold">Danışana gönderilen PDF’ler</div>
              <div className="text-[11px] text-pleiades-muted mt-1">Her çalışma yalnızca davet erişimi aktif olan bu danışanın Çalışmalarım alanında görünür.</div>
            </div>
          </div>
          {workAssignments.archive.length > 0 && <section className="bg-white border border-pleiades-border mb-4">
            <div className="px-4 py-3 border-b border-pleiades-border text-[12px] font-semibold">Çalışma geçmişi</div>
            {workAssignments.archive.map((item) => <div key={item.assignment_id} className="px-4 py-3 border-b last:border-b-0 border-pleiades-border text-[12px]">
              <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-2"><div className="font-medium">{item.title}</div><div className="text-[10px] uppercase tracking-wider text-pleiades-muted">{item.assignment_status === "cancelled_by_admin" ? "Rehber tarafından sonlandırıldı" : item.assignment_status === "superseded_by_admin" ? "Yeni çalışma ile değiştirildi" : item.assignment_status === "archived_legacy" ? "Sistem geçişinde arşivlendi" : "Danışan tarafından tamamlandı"}</div></div>
              <div className="text-[11px] text-pleiades-muted mt-1">{formatDateTime(item.completed_at || item.cancelled_at || item.superseded_at || item.archived_at)}</div>
              {item.cancellation_reason_public && <div className="text-[11px] text-pleiades-slate mt-2">{item.cancellation_reason_public}</div>}
              {item.archive_reason_public && <div className="text-[11px] text-pleiades-slate mt-2">{item.archive_reason_public}</div>}
            </div>)}
          </section>}
          {workAssignments.active && <section className="bg-white border border-pleiades-border p-5 mb-4">
            <div className="flex flex-col md:flex-row md:items-start md:justify-between gap-5">
              <div className="min-w-0">
                <div className="text-[10px] uppercase tracking-[.14em] text-pleiades-blue">Mevcut çalışma</div>
                <h3 className="text-[18px] font-semibold mt-2">{workAssignments.active.title}</h3>
                <p className="text-[12px] text-pleiades-slate mt-2 max-w-[70ch]">{workAssignments.active.description || "Açıklama eklenmedi."}</p>
                <dl className="grid grid-cols-2 sm:grid-cols-4 gap-4 mt-4 text-[11px]">
                  <div><dt className="text-pleiades-muted">Durum</dt><dd className="mt-1 font-medium">{workAssignments.active.can_complete ? "Tamamlamaya uygun" : "Çalışma devam ediyor"}</dd></div>
                  <div><dt className="text-pleiades-muted">Başlangıç</dt><dd className="mt-1">{formatDateTime(workAssignments.active.assigned_at)}</dd></div>
                  <div><dt className="text-pleiades-muted">Uygun tarih</dt><dd className="mt-1">{formatDateTime(workAssignments.active.eligible_at)}</dd></div>
                  <div><dt className="text-pleiades-muted">Deneyim</dt><dd className="mt-1">{experiences.filter((item) => item.assignment_id === workAssignments.active.assignment_id).length} kayıt</dd></div>
                </dl>
              </div>
              {user?.role === "admin" && <button type="button" onClick={() => { setCancelTarget(workAssignments.active); setCancelReason(""); }} className="min-h-10 px-4 border border-red-200 text-red-700 text-[12px] hover:bg-red-50 shrink-0">Çalışmayı sonlandır</button>}
            </div>
          </section>}
          {specialPackageActive && user?.role === "admin" ? <form ref={pdfUploadFormRef} onSubmit={requestPdfPublish} className="bg-white border border-pleiades-border p-4 mb-4">
            <div className="mb-4">
              <div className="text-[13px] font-semibold">{workAssignments.active ? "Yeni çalışma PDF’si ile değiştir" : "Yeni çalışma PDF’si yayınla"}</div>
              <p className="text-[11px] leading-relaxed text-pleiades-muted mt-1">
                {workAssignments.active
                  ? "Yeni PDF onayınızdan sonra aktif olur. Mevcut çalışma, PDF’si ve danışanın deneyimleri korunarak arşive taşınır."
                  : "PDF yayınlandığında danışanın aktif çalışma alanında hemen görünür."}
              </p>
            </div>
            <div className="grid md:grid-cols-2 gap-3">
              <label className="block"><span className="label">Çalışma başlığı</span><input required value={pdfForm.title} onChange={(e) => setPdfForm({ ...pdfForm, title: e.target.value })} maxLength={180} className="input" placeholder="Örn. İçsel Denge Çalışması" /></label>
              <label className="block"><span className="label">Konu</span><input required value={pdfForm.topic} onChange={(e) => setPdfForm({ ...pdfForm, topic: e.target.value })} maxLength={120} className="input" placeholder="Örn. İçsel Denge" /></label>
              <label className="block"><span className="label">Çalışma süresi</span><select value={pdfForm.work_days} onChange={(e) => setPdfForm({ ...pdfForm, work_days: Number(e.target.value) })} className="input"><option value={7}>7 gün</option><option value={15}>15 gün</option><option value={30}>30 gün</option></select></label>
              <div className="border border-pleiades-border bg-pleiades-ivory px-3 py-2 text-[11px] text-pleiades-slate"><div className="font-medium text-pleiades-ink">Tamamlamaya uygun tarih</div><div className="mt-1">Yayın anından {pdfForm.work_days} gün sonra. Bu tarihten önce danışan çalışmayı tamamlayamaz.</div></div>
            </div>
            <label className="block mt-3"><span className="label">Açıklama</span><textarea value={pdfForm.description} onChange={(e) => setPdfForm({ ...pdfForm, description: e.target.value })} rows={3} maxLength={3000} className="input py-3 resize-y" placeholder="Çalışmanın amacı ve uygulanışı…" /></label>
            <div className="grid md:grid-cols-[1fr_auto] gap-3 items-end mt-3">
              <label className="block"><span className="label">PDF dosyası</span><input required type="file" accept="application/pdf,.pdf" onChange={(e) => setPdfForm({ ...pdfForm, file: e.target.files?.[0] || null })} className="block w-full text-[12px] file:min-h-10 file:px-4 file:border-0 file:bg-pleiades-ivory file:mr-3" /></label>
              <button disabled={uploading} className="min-h-11 px-4 bg-pleiades-blue text-white text-[12px] flex items-center justify-center gap-2 disabled:opacity-50"><Upload className="w-3.5 h-3.5" />{uploading ? "Yükleniyor…" : workAssignments.active ? "Yeni PDF’yi incele ve yayınla" : "Çalışmayı yayınla"}</button>
            </div>
          </form> : !client.is_special_client ? <div className="mb-4 border border-amber-200 bg-amber-50 text-amber-800 p-4 text-[12px]">Çalışma PDF’si yüklemek için danışanın kendisine verilen davet kodunu kullanması gerekir.</div> : !specialPackageActive ? <div className="mb-4 border border-amber-200 bg-amber-50 text-amber-900 p-4 text-[12px]">Bu danışanın davet erişimi aktif değil. Yeni çalışma yayınlamadan önce yeni bir davet kodu oluşturun.</div> : user?.role !== "admin" ? <div className="mb-4 border border-pleiades-border bg-pleiades-ivory p-4 text-[12px]">Kişisel çalışma PDF’sini yalnızca admin yayınlayabilir.</div> : null}
          <div className="border border-pleiades-border bg-white rounded-sm overflow-hidden">
            {files.length === 0 && <EmptyState text="Bu danışana henüz PDF yüklenmemiş." />}
            {files.map((file) => (
              <div key={file.id} className="flex items-center gap-3 px-4 py-3 border-b border-pleiades-border last:border-b-0">
                <div className="w-9 h-9 grid place-items-center bg-red-50 text-red-700"><FileText className="w-4 h-4" /></div>
                <div className="min-w-0 flex-1">
                  <div className="text-[13px] font-medium truncate">{file.title || file.filename}</div>
                  <div className="text-[11px] text-pleiades-muted mt-0.5">
                    {file.uploaded_at ? formatDateTime(file.uploaded_at) : "—"} · {file.size ? `${Math.ceil(file.size / 1024)} KB` : "—"}{file.work_days ? ` · ${file.work_days} gün çalışma${file.rest_days ? ` + ${file.rest_days} gün dinlenme` : ""}` : ""}
                  </div>
                </div>
                <button onClick={() => openFile(file)} className="h-8 px-3 border border-pleiades-border text-[12px] flex items-center gap-1.5 hover:bg-pleiades-sidebar">
                  <Download className="w-3.5 h-3.5" /> Aç
                </button>
              </div>
            ))}
          </div>
        </div>
      )}

      <ClientEditDialog
        open={editOpen}
        onOpenChange={setEditOpen}
        client={client}
        onSaved={(updated) => setClient((c) => ({ ...c, ...updated }))}
      />
      <NewAppointmentDialog
        open={apptOpen}
        onOpenChange={setApptOpen}
        defaultClientId={id}
        onCreated={(a) => setAppts((prev) => [a, ...prev])}
      />
      <NewTaskDialog
        open={taskOpen}
        onOpenChange={setTaskOpen}
        defaultClientId={id}
        onCreated={(t) => setTasks((prev) => [t, ...prev])}
      />
      <NewPaymentDialog
        open={payOpen}
        onOpenChange={setPayOpen}
        defaultClientId={id}
        onCreated={(p) => setPayments((prev) => [p, ...prev])}
      />
      <MessageComposeDialog
        open={msgOpen}
        onOpenChange={setMsgOpen}
        defaultClientId={id}
        onSent={(m) => setMessages((prev) => [m, ...prev])}
      />
      <Dialog open={Boolean(cancelTarget)} onOpenChange={(open) => !open && !cancelling && setCancelTarget(null)}>
        <DialogContent className="max-w-md rounded-none border-pleiades-border bg-white">
          <DialogHeader>
            <DialogTitle>Çalışmayı sonlandır</DialogTitle>
            <DialogDescription className="text-pleiades-slate leading-relaxed">
              Çalışma arşivlenecek ve yeni çalışma yayınlama kilidi açılacak. PDF ve deneyimler silinmeyecek.
            </DialogDescription>
          </DialogHeader>
          <label className="block">
            <span className="label">Danışana gösterilecek açıklama</span>
            <textarea autoFocus required minLength={5} maxLength={1000} rows={5} value={cancelReason} onChange={(event) => setCancelReason(event.target.value)} className="input py-3 resize-y" placeholder="Örn. Çalışma içeriği güncelleneceği için rehber tarafından sonlandırıldı." />
          </label>
          <DialogFooter>
            <button type="button" disabled={cancelling} onClick={() => setCancelTarget(null)} className="min-h-10 px-4 border border-pleiades-border text-[12px] disabled:opacity-50">Geri dön</button>
            <button type="button" disabled={cancelling || cancelReason.trim().length < 5} onClick={cancelActiveWork} className="min-h-10 px-4 bg-red-700 text-white text-[12px] disabled:opacity-50">{cancelling ? "Sonlandırılıyor…" : "Çalışmayı sonlandır"}</button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
      <Dialog open={Boolean(replaceTarget)} onOpenChange={(open) => !open && !uploading && setReplaceTarget(null)}>
        <DialogContent className="max-w-md rounded-none border-pleiades-border bg-white">
          <DialogHeader>
            <DialogTitle>Aktif çalışmayı yeni PDF ile değiştir</DialogTitle>
            <DialogDescription className="text-pleiades-slate leading-relaxed">
              “{replaceTarget?.title}” silinmeden arşive taşınacak; PDF ve danışanın yazdığı tüm deneyimler korunacak.
              “{pdfForm.title || pdfForm.file?.name}” yeni aktif çalışma olarak yayınlanacak.
            </DialogDescription>
          </DialogHeader>
          <div className="border border-amber-200 bg-amber-50 px-4 py-3 text-[12px] leading-relaxed text-amber-950">
            Bu işlem danışanın aktif çalışma süresini yeni PDF’nin yayınlandığı anda yeniden başlatır.
          </div>
          <DialogFooter>
            <button type="button" disabled={uploading} onClick={() => setReplaceTarget(null)} className="min-h-10 px-4 border border-pleiades-border text-[12px] disabled:opacity-50">Geri dön</button>
            <button type="button" disabled={uploading} onClick={() => publishPdf(replaceTarget)} className="min-h-10 px-4 bg-pleiades-blue text-white text-[12px] disabled:opacity-50">
              {uploading ? "Yayınlanıyor…" : "Onayla ve yeni PDF’yi yayınla"}
            </button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}

function EmptyState({ text }) {
  return (
    <div className="text-center text-[12px] text-pleiades-slate py-10 border border-dashed border-pleiades-border rounded-sm">
      {text}
    </div>
  );
}
